import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../data/cakes';
import { api } from '../utils/api';
import './Cart.css';

const Cart = () => {
  const { cart, updateQuantity, updateWeight, removeItem, getTotals, fetchSettings, orderType, setOrderType, addAddon, removeAddon } = useCart();
  const navigate = useNavigate();
  const totals = getTotals(orderType);
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(true);

  useEffect(() => {
    fetchSettings();
    loadAddons();
  }, []);

  const loadAddons = async () => {
    try {
      const data = await api.get('/addons.php');
      if (data.success && data.data && data.data.addons) {
        setAddons(data.data.addons.filter(addon => addon.is_available === 1));
      }
    } catch (error) {
      console.error('Error loading addons:', error);
    } finally {
      setLoadingAddons(false);
    }
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    updateQuantity(itemId, newQuantity);
  };

  const handleWeightChange = (itemId, newWeight) => {
    updateWeight(itemId, parseFloat(newWeight));
  };

  const handleRemoveItem = (itemId) => {
    if (window.confirm('Remove this item from cart?')) {
      removeItem(itemId);
    }
  };

  const handleAddAddon = (addon) => {
    addAddon(addon);
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) return;
    navigate('/checkout');
  };

  // Separate cakes and addons
  const cakeItems = cart.items.filter(item => item.type !== 'addon');
  const addonItems = cart.items.filter(item => item.type === 'addon');

  if (cart.items.length === 0) {
    return (
      <div className="cart-page">
        <header className="cart-header glass-effect">
          <div className="cart-header-content">
            <Link to="/" className="back-button ripple">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </Link>
            <h1 className="cart-title">Shopping Cart</h1>
          </div>
        </header>

        <div className="empty-cart fade-in">
          <div className="empty-cart-icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Add some delicious cakes to get started!</p>
          <Link to="/" className="btn btn-primary">
            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
            </svg>
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <header className="cart-header glass-effect">
        <div className="cart-header-content">
          <Link to="/" className="back-button ripple">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </Link>
          <h1 className="cart-title">Shopping Cart</h1>
          <div className="cart-count-badge">{cart.items.length}</div>
        </div>
      </header>

      <div className="cart-content">
        <div className="container">
          <div className="cart-layout">
            {/* Cart Items */}
            <div className="cart-items-section">
              <div className="cart-items">
                {cakeItems.map((item, index) => (
                  <div key={item.id} className="cart-item card fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="cart-item-image">
                      {item.cakeImage ? (
                        <img src={getImageUrl(item.cakeImage)} alt={item.cakeTitle} />
                      ) : (
                        <div className="cart-item-placeholder gradient-secondary">
                          <span>🎂</span>
                        </div>
                      )}
                    </div>

                    <div className="cart-item-details">
                      <h3 className="cart-item-title">{item.cakeTitle}</h3>
                      <div className="cart-item-meta">
                        <div className="meta-badge weight-selector">
                          <svg className="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path>
                          </svg>
                          <select 
                            className="weight-select"
                            value={item.weight}
                            onChange={(e) => handleWeightChange(item.id, e.target.value)}
                          >
                            {(() => {
                              const options = [];
                              const baseWeight = item.basePrice ? 0.5 : 0.5; // Start from 0.5kg
                              for (let w = baseWeight; w <= 10; w += 0.5) {
                                options.push(
                                  <option key={w} value={w}>{w.toFixed(1)}kg</option>
                                );
                              }
                              return options;
                            })()}
                          </select>
                        </div>
                        <span className="meta-badge">
                          <svg className="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                          </svg>
                          {item.flavourName}
                        </span>
                      </div>
                      {item.cakeMessage && (
                        <div className="cart-item-message">
                          <span className="message-icon">✍️</span>
                          <span className="message-text">"{item.cakeMessage}"</span>
                        </div>
                      )}
                      <div className="cart-item-price">₹{item.unitPrice.toLocaleString()}</div>
                    </div>

                    <div className="cart-item-actions">
                      <div className="quantity-control">
                        <button 
                          className="quantity-btn ripple"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"></path>
                          </svg>
                        </button>
                        <span className="quantity-value">{item.quantity}</span>
                        <button 
                          className="quantity-btn ripple"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                          </svg>
                        </button>
                      </div>

                      <button 
                        className="remove-btn ripple"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>

                      <div className="cart-item-subtotal">
                        ₹{item.subtotal.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Addon Items in Cart */}
                {addonItems.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', marginBottom: '0.5rem', paddingLeft: '1rem' }}>Added Extras</h3>
                    {addonItems.map((item, index) => (
                      <div key={item.id} className="cart-item card fade-in" style={{ animationDelay: `${(cakeItems.length + index) * 0.1}s` }}>
                        <div className="cart-item-image">
                          {item.addonImage ? (
                            <img src={getImageUrl(item.addonImage)} alt={item.addonName} />
                          ) : (
                            <div className="cart-item-placeholder gradient-secondary">
                              <span>🎁</span>
                            </div>
                          )}
                        </div>

                        <div className="cart-item-details">
                          <h3 className="cart-item-title">{item.addonName}</h3>
                          <div className="cart-item-meta">
                            <span className="meta-badge" style={{ fontSize: '0.75rem', color: '#999' }}>{item.categoryName}</span>
                          </div>
                          <div className="cart-item-price">₹{item.unitPrice.toLocaleString()}</div>
                        </div>

                        <div className="cart-item-actions">
                          <div className="quantity-control">
                            <button 
                              className="quantity-btn ripple"
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            >
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"></path>
                              </svg>
                            </button>
                            <span className="quantity-value">{item.quantity}</span>
                            <button 
                              className="quantity-btn ripple"
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            >
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                              </svg>
                            </button>
                          </div>

                          <button 
                            className="remove-btn ripple"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>

                          <div className="cart-item-subtotal">
                            ₹{item.subtotal.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Addons Section */}
              {addons.length > 0 && (
                <div className="addons-section" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #f0f0f0' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#4a312d', marginBottom: '1rem', paddingLeft: '1rem' }}>Add Party Essentials</h3>
                  <div style={{ 
                    display: 'flex', 
                    overflowX: 'auto', 
                    gap: '1rem', 
                    paddingBottom: '1rem',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}>
                    <style>{`
                      .addons-section > div::-webkit-scrollbar {
                        display: none;
                      }
                    `}</style>
                    {addons.map(addon => (
                      <div key={addon.id} className="addon-card" style={{
                        minWidth: '140px',
                        maxWidth: '140px',
                        background: '#fff',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        flexShrink: 0
                      }}
                      onClick={() => handleAddAddon(addon)}
                      >
                        <div style={{ aspectRatio: '1', overflow: 'hidden', background: '#f5f5f5' }}>
                          <img 
                            src={getImageUrl(addon.image)} 
                            alt={addon.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { 
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-size="40" text-anchor="middle" dy=".3em"%3E🎁%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                        <div style={{ padding: '0.75rem' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#333', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {addon.name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: '0.5rem' }}>
                            {addon.category_name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#9c6b5f' }}>
                              ₹{addon.price}
                            </span>
                            <button 
                              style={{
                                background: '#9c6b5f',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                lineHeight: 1
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="order-summary-section">
              <div className="order-summary card">
                <h2 className="summary-title">Order Summary</h2>
                
                {/* Delivery/Pickup Toggle */}
                <div className="delivery-type-selector">
                  <button 
                    className={`delivery-type-btn ${orderType === 'delivery' ? 'active' : ''}`}
                    onClick={() => setOrderType('delivery')}
                  >
                    <svg className="delivery-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    Delivery
                  </button>
                  <button 
                    className={`delivery-type-btn ${orderType === 'pickup' ? 'active' : ''}`}
                    onClick={() => setOrderType('pickup')}
                  >
                    <svg className="delivery-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                    Pickup
                  </button>
                </div>

                {/* Order Summary Details (Subtotal, Tax, etc) */}
                <div className="order-summary-details">
                    <div className="summary-row">
                      <span>Subtotal</span>
                      <span className="summary-value">₹{totals.subtotal.toLocaleString()}</span>
                    </div>

                    {totals.tax > 0 && (
                      <div className="summary-row">
                        <span>Tax ({totals.settings?.tax_percentage}%)</span>
                        <span className="summary-value">₹{totals.tax.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span>Delivery Fee</span>
                      <span className="summary-value">
                        {totals.deliveryFee === 0 ? (
                           <span style={{ color: 'green' }}>Free</span>
                        ) : (
                           `₹${totals.deliveryFee}`
                        )}
                      </span>
                    </div>
                </div>

                {/* Total Display (Scrolls with page) */}
                <div className="summary-total">
                  <span>Total</span>
                  <span className="total-value">₹{totals.total.toFixed(2)}</span>
                </div>

                {/* Sticky Bottom Button */}
                <div className="cart-bottom-bar">
                    <button 
                      className="checkout-btn btn-primary ripple"
                      onClick={handleCheckout}
                    >
                      Proceed to Checkout
                    </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
